import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { streamChatMessage } from "~/ai-chat/data-source/stream-chat-message";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { useChatContext } from "~/ai-chat/feature/use-chat-context";
import { serializeChatContext } from "~/ai-chat/model/chat-context";
import type { ChatError, ChatMessage, ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import { ENTITY_LINK_INSTRUCTIONS } from "~/ai-chat/model/entity-link";

interface ChatSendApi {
  send: (content: string) => Promise<void>;
  isPending: boolean;
  // A short "Using <tool>…" label while the agent is running a tool, or null.
  activity: string | null;
}

// "list_systems" -> "list systems" for a friendlier activity label.
function humanizeToolName(name: string): string {
  return name.replace(/_/g, " ");
}

interface BackendErrorInfo {
  status: number | null;
  detail: string | null;
}

// Errors thrown by the SDK on non-2xx are `ResponseError` instances exposing a
// `response: Response`. Duck-type to avoid coupling to the runtime export, then
// pull the backend's `detail` field (FastAPI convention) and HTTP status.
async function extractBackendError(error: unknown): Promise<BackendErrorInfo> {
  const empty: BackendErrorInfo = { status: null, detail: null };
  if (error === null || typeof error !== "object") return empty;
  const response = (error as { response?: unknown }).response;
  if (!(response instanceof Response)) return empty;
  let detail: string | null = null;
  try {
    const body = await response.clone().json();
    if (body !== null && typeof body === "object" && typeof (body as { detail?: unknown }).detail === "string") {
      detail = (body as { detail: string }).detail;
    }
  } catch {
    // Body wasn't JSON; leave detail null.
  }
  return { status: response.status, detail };
}

function onlyMessages(items: ChatTranscriptItem[]): ChatMessage[] {
  return items.filter((item): item is ChatMessage => item.kind === "message");
}

export function useChatSend(): ChatSendApi {
  const { messages, appendMessage, updateMessage, markUnavailable, markMcpToolsAvailable } = useAiChat();
  const chatContext = useChatContext();
  const [isPending, setIsPending] = useState(false);
  const [activity, setActivity] = useState<string | null>(null);

  // Abort the in-flight stream when the chat unmounts (e.g. the window is
  // closed) so the backend agent doesn't keep running — and burning tokens —
  // for a reply no one will see.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback(
    async (content: string) => {
      const trimmed = content.trim();
      if (trimmed.length === 0 || isPending) return;

      const userMessage: ChatMessage = {
        kind: "message",
        id: crypto.randomUUID(),
        role: "user",
        content: trimmed,
      };
      const conversation = [...onlyMessages(messages), userMessage];
      appendMessage(userMessage);
      setIsPending(true);
      setActivity(null);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // The assistant message is created on the first `delta` so an empty bubble
      // never flashes; later deltas append to it in place.
      let assistantId: string | null = null;
      // The stream can deliver a terminal `error` event; track it so the catch
      // block below doesn't also append a duplicate error bubble.
      let sawStreamError = false;

      try {
        const systemPrompt = [serializeChatContext(chatContext), ENTITY_LINK_INSTRUCTIONS].join("\n\n");
        await streamChatMessage({
          messages: conversation,
          systemPrompt,
          signal: controller.signal,
          onEvent: (event) => {
            switch (event.type) {
              case "delta": {
                const currentId = assistantId;
                if (currentId === null) {
                  const id = crypto.randomUUID();
                  assistantId = id;
                  appendMessage({ kind: "message", id, role: "assistant", content: event.content });
                } else {
                  updateMessage(currentId, (item) =>
                    item.kind === "message" ? { ...item, content: item.content + event.content } : item,
                  );
                }
                setActivity(null);
                break;
              }
              case "toolCall": {
                setActivity(event.status === "started" ? `Using ${humanizeToolName(event.name)}…` : null);
                break;
              }
              case "done": {
                if (event.mcpServerUrl !== null) markMcpToolsAvailable();
                break;
              }
              case "error": {
                sawStreamError = true;
                appendMessage({ kind: "error", id: crypto.randomUUID(), content: event.message });
                break;
              }
            }
          },
        });
      } catch (error) {
        // Intentional cancellation (chat closed / superseded) — not a failure.
        if (controller.signal.aborted) return;
        const { status, detail } = await extractBackendError(error);
        if (status === 404) {
          markUnavailable();
          toast.error("Istari AI is not configured on this server.", { duration: 8000 });
          return;
        }
        if (!sawStreamError) {
          const errorItem: ChatError = {
            kind: "error",
            id: crypto.randomUUID(),
            content: detail ?? "Failed to send message. Please try again.",
          };
          appendMessage(errorItem);
        }
      } finally {
        setIsPending(false);
        setActivity(null);
      }
    },
    [messages, appendMessage, updateMessage, markUnavailable, markMcpToolsAvailable, chatContext, isPending],
  );

  return { send, isPending, activity };
}
